import { ChangeDetectorRef, DestroyRef, inject, Injectable, Injector, NgZone, PlatformRef, ViewRef } from '@angular/core';
import { ProcessorService } from '@df/ng/processor.service';

const globalObject = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : undefined;

const nativeSetTimeout = (globalObject && globalObject['__zone_symbol__setTimeout']) || setTimeout;
const nativeSetInterval = (globalObject && globalObject['__zone_symbol__setInterval']) || setInterval;

export enum EAsyncPriority {
    // immediately, but non-blocking
    immeditate,
    // on processor ready
    bootstrap,
    // when app is idle
    idle,
    // later
    low
}

interface Cancelable {
    cancel(): void;
}

/**
 * Disposable functions are a way to manage timers, intervals, and event listeners
 * that should be cleared when a component is destroyed.
 *
 * @example
 * ```ts
 * private readonly timeoutService = injectTimeoutService();
 *
 * this.timeoutService.setTimeout(() => {
 *  // do something
 * }, 1000);
 * ```
 *
 * If you want to manually clear the timeout
 * ```ts
 * const timeout = this.timeoutService.setTimeout(() => {
 *  // do something
 * }, 1000);
 *
 * // clear the timeout
 * timeout();
 * ```
 */
export function injectTimeoutService() {
    const destroyRef = inject(DestroyRef);
    const viewRef = inject(ChangeDetectorRef, { optional: true }) as ViewRef;
    const timeoutService = inject(TimeoutService);

    let destroyed = false;
    destroyRef.onDestroy(() => {
        destroyed = true;
    });

    // NOTE: subscribing to onDesroy is insufficient as it is being called after the component is destroyed
    const isDestroyed = (): boolean => destroyed || !!viewRef?.destroyed;

    return {
        platformSafe: (cb: () => void | any, inZone = true) => {
            return timeoutService.platformSafe(cb, inZone);
        },

        async: (callback: () => void, priority: EAsyncPriority = EAsyncPriority.immeditate, options?: { timeout?: number }) => {
            if (isDestroyed()) {
                return () => {};
            }

            const result = timeoutService.async(callback, priority, options);

            destroyRef.onDestroy(() => {
                if (result && typeof result === 'function') {
                    result.cancel();
                }
            });

            return result;
        },

        /**
         * Set a timeout that will be cleared when the component is destroyed.
         * @param callback The callback to execute
         * @param delay The delay before the callback is executed
         * @returns A function to clear the timeout
         */
        setTimeout: (callback: () => void, delay = 0, inZone = true) => {
            if (isDestroyed()) {
                return undefined;
            }

            const result = timeoutService.setTimeout(callback, delay, inZone);

            destroyRef.onDestroy(() => {
                clearTimeout(result);
            });

            return result;
        },

        /**
         * Set an interval that will be cleared when the component is destroyed.
         * @param callback The callback to execute
         * @param delay The delay before the callback is executed
         * @returns A function to clear the interval
         */
        setInterval: (callback: () => void, delay: number) => {
            if (isDestroyed()) {
                return undefined;
            }

            const result = timeoutService.setInterval(callback, delay);

            destroyRef.onDestroy(() => {
                clearInterval(result);
            });

            return result;
        },

        debounce: <T extends (...args: any[]) => any>(func: T, wait: number = 0, immediate?: boolean) => {
            if (isDestroyed()) {
                return () => {};
            }

            const result = timeoutService.debounce(func, wait, immediate);

            destroyRef.onDestroy(() => {
                if (result && typeof result === 'function') {
                    result.cancel();
                }
            });

            return result;
        },

        throttle: <T extends (...args: any[]) => any>(
            func: T,
            wait: number = 0,
            options: { leading?: boolean; trailing?: boolean } = {}
        ) => {
            if (isDestroyed()) {
                return () => {};
            }

            const result = timeoutService.throttle(func, wait, options);

            destroyRef.onDestroy(() => {
                if (result && typeof result === 'function') {
                    result.cancel();
                }
            });

            return result;
        },

        /**
         * Set an interval that will be cleared when the component is destroyed.
         * @param callback The callback to execute
         * @param delay The delay before the callback is executed
         * @param target
         * @param type
         * @param listener
         * @param options
         * @returns A function to clear the interval
         */
        addEventListener: <K extends keyof HTMLElementEventMap>(
            target: EventTarget,
            type: K,
            listener: (this: HTMLElement, ev: HTMLElementEventMap[K]) => void,
            options?: boolean | AddEventListenerOptions
        ) => {
            if (isDestroyed()) {
                return () => {};
            }

            target.addEventListener(type, listener as EventListenerOrEventListenerObject, options);
            const cleanup = () => target.removeEventListener(type, listener as EventListenerOrEventListenerObject, options);

            destroyRef.onDestroy(cleanup);

            return cleanup;
        },

        /**
         * Set a requestAnimationFrame that will be cleared when the component is destroyed.
         * @param callback The callback to execute
         * @returns A function to clear the requestAnimationFrame
         */
        requestAnimationFrame: (callback: FrameRequestCallback) => {
            if (isDestroyed()) {
                return () => {};
            }

            const id = requestAnimationFrame(callback);
            const cleanup = () => cancelAnimationFrame(id);

            destroyRef.onDestroy(cleanup);

            return cleanup;
        }
    };
}

@Injectable({
    providedIn: 'root'
})
export class TimeoutService {
    readonly ngZone = inject(NgZone);
    private readonly platformRef = inject(PlatformRef);

    platformSafe(cb: () => void | any, inZone = true) {
        const injector = ProcessorService.injector as Injector & { destroyed?: boolean };

        if (!this.platformRef.destroyed && !injector?.destroyed) {
            if (inZone) {
                this.ngZone.run(cb);
            } else {
                this.ngZone.runOutsideAngular(cb);
            }
        }
    }

    /**
     * Perform operation asynchronously
     */
    async(cb: () => void, priority: EAsyncPriority = EAsyncPriority.immeditate, options?: { timeout?: number }) {
        switch (priority) {
            case EAsyncPriority.bootstrap:
                return this.async(() => ProcessorService.onReady(() => this.async(() => cb())));
            case EAsyncPriority.immeditate:
                return this.setTimeout(cb);
            case EAsyncPriority.low:
                return this.async(cb, EAsyncPriority.idle, { timeout: 8000 });
            case EAsyncPriority.idle:
                if (typeof window !== 'undefined' && window['requestIdleCallback']) {
                    window['requestIdleCallback'](() => this.platformSafe(cb), options?.timeout ? { timeout: options.timeout } : undefined);
                } else {
                    this.setTimeout(cb, options?.timeout || 5000);
                }
        }
    }

    /**
     * Sets timeout outside angular zone
     */
    setTimeout(cb: () => void, delay?: number, inZone = true): number {
        return <number>(<any>nativeSetTimeout(
            () => this.platformSafe(cb, inZone),

            delay
        ));
    }

    setInterval(cb: () => void, delay?: number): number {
        const intervalId = <number>(<any>nativeSetInterval(() => {
            if (!this.platformRef.destroyed) {
                cb();
            } else {
                clearInterval(intervalId);
            }
        }, delay));

        return intervalId;
    }

    /**
     * Returns debounced method
     */
    debounce<T extends (...args: any[]) => any>(func: T, wait: number = 0, immediate?: boolean): T & Cancelable {
        const that = this;
        let timeout: number | undefined;
        let previous = 0;
        let args: any[] | undefined;
        let result: T | undefined;
        let context: any;

        const later = function () {
            const passed = new Date().getTime() - previous;
            if (wait > passed) {
                timeout = that.setTimeout(later, wait - passed);
            } else {
                timeout = undefined;
                if (!immediate) result = func.apply(context, args || []);
                // This check is needed because `func` can recursively invoke `debounced`.
                if (!timeout) args = context = undefined;
            }
        };

        const debounced = <any>that.restArguments(function (this: any, _args: any) {
            context = this;
            args = _args;
            previous = new Date().getTime();
            if (!timeout) {
                timeout = that.setTimeout(later, wait);
                if (immediate) result = func.apply(context, args || []);
            }
            return result;
        });

        debounced.cancel = function () {
            clearTimeout(timeout);
            timeout = args = context = undefined;
        };

        return debounced;
    }

    /**
     * Exact copy of underscore throttle method
     * changed setTimeout to use ngZone protected version
     */
    throttle<T extends (...args: any[]) => any>(
        func: T,
        wait: number = 0,
        options: { leading?: boolean; trailing?: boolean } = {}
    ): T & Cancelable {
        const that = this;

        let timeout: number | undefined;
        let context: any;
        let args;
        let result;
        let previous = 0;

        const later = function () {
            previous = options.leading === false ? 0 : new Date().getTime();
            timeout = undefined;
            result = func.apply(context, args);
            if (!timeout) context = args = null;
        };

        const throttled = function (this: any) {
            const _now = new Date().getTime();
            if (!previous && options.leading === false) previous = _now;
            const remaining = wait - (_now - previous);
            context = this;
            args = arguments;
            if (remaining <= 0 || remaining > wait) {
                if (timeout) {
                    clearTimeout(timeout);
                    timeout = undefined;
                }
                previous = _now;
                result = func.apply(context, args);
                if (!timeout) {
                    context = args = undefined;
                }
            } else if (!timeout && options.trailing !== false) {
                timeout = that.setTimeout(later, remaining);
            }
            return result;
        };

        throttled.cancel = function () {
            clearTimeout(timeout);
            previous = 0;
            timeout = context = args = undefined;
        };

        return throttled as unknown as T & Cancelable;
    }

    private restArguments(func, _startIndex?: number) {
        const startIndex = typeof _startIndex === 'number' ? _startIndex : func.length - 1;
        return function (this: any) {
            const length = Math.max(arguments.length - startIndex, 0);
            const rest = Array(length);
            let index = 0;
            for (; index < length; index++) {
                rest[index] = arguments[index + startIndex];
            }
            switch (startIndex) {
                case 0:
                    return func.call(this, rest);
                case 1:
                    return func.call(this, arguments[0], rest);
                case 2:
                    return func.call(this, arguments[0], arguments[1], rest);
            }
            const args = Array(startIndex + 1);
            for (index = 0; index < startIndex; index++) {
                args[index] = arguments[index];
            }
            args[startIndex] = rest;
            return func.apply(this, args);
        };
    }
}
